import { Component } from "@mzw/component";
import { scheduler } from "@mzw/utilities";

export class LazyVideo extends Component {
	#abortController = new AbortController();
	#intersectionObserver;
	/** @type {HTMLVideoElement} */
	video;

	/**
	 * Returns whether the video is currently playing.
	 * @returns {boolean}
	 */
	get playing() {
		return !this.video?.paused;
	}

	constructor() {
		super();

		this.reloadQueued = false;
	}

	connectedCallback() {
		super.connectedCallback();
		this.video = this.querySelector("video");
		this.#intersectionObserver = new IntersectionObserver(
			(entries) => {
				const prefersReducedMotion = globalThis.matchMedia(
					"(prefers-reduced-motion: reduce)",
				).matches;

				for (const entry of entries) {
					if (entry.isIntersecting) {
						if (prefersReducedMotion) continue;
						if (entry.target.dataset.loaded) {
							if (entry.target.hasAttribute("user-paused")) continue;
							entry.target.play();
						} else {
							entry.target.loadContent();
						}
					} else {
						entry.target.pause();
					}
				}
			},
			{
				threshold: 0.3,
				rootMargin: "200px 0px",
			},
		);
		this.#intersectionObserver.observe(this);
		this.addEventListener("command", this, {
			signal: this.#abortController.signal,
		});
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.#intersectionObserver?.disconnect();
		this.#abortController?.abort();
	}

	/**
	 * Handles invoker command events for play/pause and audio toggle.
	 * @param {CommandEvent} event - The invoker command event
	 * @see https://developer.mozilla.org/en-US/docs/Web/API/CommandEvent
	 */
	handleEvent(event) {
		if (event.type === "command") {
			switch (event.command) {
				case "--toggle-playing": {
					if (this.video?.paused) this.play();
					else this.pause();
					this.toggleAttribute("user-paused", !this.playing);
					break;
				}

				case "--toggle-audio": {
					this.video.muted = !this.video.muted;
					break;
				}
			}
		}
	}

	/**
	 * Loads the video content from the template and starts playback.
	 */
	async loadContent() {
		if (this.dataset.loaded) return;

		const content = this.querySelector("template")?.content.firstElementChild?.cloneNode(true);

		if (content) {
			(this.querySelector("a") ?? this).append(content);
			// Re-query video reference after template content is added
			this.video = this.querySelector("video");
		}

		this.dataset.loaded = true;
		this.dispatchEvent(new CustomEvent("media:loaded"));
		this.addEventListeners();

		if (this.video instanceof HTMLVideoElement) {
			this.video.setAttribute("preload", "auto");
			this.video.setAttribute("autoplay", "");
			this.video.load();
			this.video.addEventListener(
				"canplay",
				() => {
					this.play();
				},
				{ once: true },
			);
		}
	}

	addEventListeners = () => {
		if (!this.video) return;

		this.video.addEventListener(
			"play",
			() => {
				this.toggleAttribute("data-playing", true);
			},
			{ signal: this.#abortController.signal },
		);

		this.video.addEventListener(
			"pause",
			() => {
				this.toggleAttribute("data-playing", false);
			},
			{ signal: this.#abortController.signal },
		);

		for (const source of this.video.querySelectorAll("source")) {
			if (!source.media) continue;
			const mqListener = async () => {
				if (
					(source.src === this.video.currentSrc ||
						!this.previousSourcePlaying(source, this.video.currentSrc)) &&
					!this.reloadQueued
				) {
					this.reloadVideo();
				}
			};
			const matchMedia = globalThis.matchMedia(source.media);
			matchMedia.addEventListener("change", mqListener, {
				signal: this.#abortController.signal,
			});
		}
	};

	/**
	 * Checks if a previous sibling source element is currently playing.
	 * @param {HTMLSourceElement} source - The source element to check
	 * @param {string} currentSource - The currently playing source URL
	 * @returns {boolean} Whether a previous sibling is the current source
	 */
	previousSourcePlaying(source, currentSource) {
		let sibling = source.previousElementSibling;
		while (sibling) {
			if (sibling.src === currentSource) return true;
			sibling = sibling.previousElementSibling;
		}
		return false;
	}

	/**
	 * Reloads the video while preserving the current playback position and state.
	 */
	reloadVideo = () => {
		this.reloadQueued = true;
		const currentTime = this.video.currentTime;
		const wasPaused = this.video.paused;
		this.video.load();
		const videoLoaded = () => {
			this.video.currentTime = currentTime;
			if (!wasPaused) this.video.play();
			this.reloadQueued = false;
			this.video.removeEventListener("loadeddata", videoLoaded);
		};
		this.video.addEventListener("loadeddata", videoLoaded);
	};

	/** Plays the video. */
	play() {
		this.video?.play();
	}

	/** Pauses the video. */
	pause() {
		this.video?.pause();
	}
}

if (!globalThis.customElements.get("lazy-video")) {
	globalThis.customElements.define("lazy-video", LazyVideo);
}

export class LazyVideoButtons extends Component {
	#abortController = new AbortController();

	requiredRefs = ["playPauseButton", "muteUnmuteButton"];

	/**
	 * Gets the lazy video element associated with this component.
	 * This could be either the command instance for the play/pause button,
	 * or the closest lazy-video ancestor element.
	 *
	 * @returns {LazyVideo|Element|null} The associated lazy video element or null if not found
	 */
	get lazyVideo() {
		return this.refs.playPauseButton?.commandForElement instanceof LazyVideo
			? this.refs.playPauseButton?.commandForElement
			: this.refs.playPauseButton?.closest("lazy-video");
	}

	connectedCallback() {
		super.connectedCallback();

		const { lazyVideo } = this;

		if (!lazyVideo) {
			console.warn("No associated <lazy-video> found for <lazy-video-buttons>.");
			return;
		}

		if (lazyVideo.dataset.loaded) {
			this.addEventListeners(lazyVideo);
		} else {
			lazyVideo.addEventListener("media:loaded", () => this.addEventListeners(lazyVideo), {
				once: true,
			});
		}
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.#abortController.abort();
	}

	addEventListeners = (lazyVideo) => {
		if (!this.refs.playPauseButton?.commandForElement) {
			this.refs.playPauseButton.commandForElement = lazyVideo;
			this.refs.muteUnmuteButton.commandForElement = lazyVideo;
		}

		for (const event of ["playing", "pause", "volumechange", "ended"]) {
			lazyVideo?.video.addEventListener(event, this.setClasses, {
				signal: this.#abortController.signal,
			});
		}

		this.checkForAudio();
	};

	setClasses = () => {
		scheduler.schedule(() => {
			this.refs.playPauseButton?.classList.toggle("paused", this.lazyVideo?.video.paused);
			this.refs.muteUnmuteButton?.classList.toggle("muted", this.lazyVideo?.video.muted);

			this.refs.playPauseButton?.setAttribute(
				"aria-label",
				this.lazyVideo?.video.paused
					? (globalThis.variantStrings.play ?? "Play")
					: (globalThis.variantStrings.pause ?? "Pause"),
			);

			this.refs.muteUnmuteButton?.setAttribute(
				"aria-label",
				this.lazyVideo?.video.muted
					? (globalThis.variantStrings.unmute ?? "Unmute")
					: (globalThis.variantStrings.mute ?? "Mute"),
			);
		});
	};

	checkForAudio = async () => {
		const source = this.lazyVideo?.video.src || this.lazyVideo?.video.currentSrc;
		if (!source) return;

		try {
			const hasAudio = await hasVideoGotAudio(source);
			this.refs.muteUnmuteButton?.classList.toggle("has-audio", hasAudio);
		} catch {
			// Video failed to load or check audio, hide mute button
			this.refs.muteUnmuteButton?.classList.remove("has-audio");
		}
	};
}

if (!globalThis.customElements.get("lazy-video-buttons")) {
	globalThis.customElements.define("lazy-video-buttons", LazyVideoButtons);
}

/**
 * Checks if a video element has audio using browser-specific APIs.
 * @param {HTMLVideoElement} video - The video element to check
 * @returns {boolean} Whether the video has audio
 */
function hasAudio(video) {
	return (
		video.mozHasAudio ||
		Boolean(video.webkitAudioDecodedByteCount) ||
		Boolean(video.audioTracks?.length)
	);
}

/**
 * Checks if a video source has audio tracks.
 * @param {string} source - The video source URL
 * @returns {Promise<boolean>} Whether the video has audio
 */
function hasVideoGotAudio(source) {
	const video = document.createElement("video");
	video.muted = true;
	video.crossOrigin = "anonymous";
	video.preload = "auto";

	const cleanup = () => {
		video.src = "";
		video.load();
	};

	return new Promise((resolve, reject) => {
		video.addEventListener(
			"error",
			(e) => {
				cleanup();
				reject(e);
			},
			{ once: true },
		);

		video.addEventListener(
			"canplay",
			() => {
				video.currentTime = 0.1;
			},
			{ once: true }, // Important because 'canplay' can be fired hundreds of times.
		);

		video.addEventListener(
			"seeked",
			() => {
				const result = hasAudio(video);
				cleanup();
				resolve(result);
			},
			{ once: true },
		);

		video.src = source;
	});
}
